import React from 'react';
import { fmt, pct, computePL } from '../utils';

function Row({ label, budgeted, actual, indent, isBenchmark, danger }) {
  const over = actual != null && budgeted != null && actual > budgeted;
  return (
    <div className={`pl-row${indent ? ' indent' : ''}${isBenchmark ? ' benchmark' : ''}`}>
      <span>{label}</span>
      <span className="dual">
        {actual != null && (
          <span className={`money ${over || danger ? 'negative' : ''}`}>{fmt(actual)}</span>
        )}
        <span className={`money projected ${over ? 'negative' : ''}`}>{fmt(budgeted)}</span>
      </span>
    </div>
  );
}

function InfoLine({ label, value, color }) {
  return (
    <div style={{display:'flex',justifyContent:'space-between',fontSize:12,padding:'3px 0'}}>
      <span style={{color:'var(--text-muted)'}}>{label}</span>
      <span style={{fontWeight:600,color: color || 'var(--text)'}}>{value}</span>
    </div>
  );
}

export default function PLDashboard({ deal }) {
  const pl = computePL(deal);

  const netColor  = pl.netBudgeted >= 0 ? 'positive' : 'negative';
  const profitOk  = pl.profitPctBudgeted >= (deal.profit_pct || 20);
  const hasPurchaseDate = !!deal.purchase_date;

  return (
    <div className="card pl-dashboard">
      <div className="section-title">P&amp;L Summary</div>

      {/* MAO box */}
      <div className="mao-box">
        <div className="mao-label">Max Allowable Offer</div>
        <div className="mao-value money">{fmt(pl.mao)}</div>
        {deal.purchase_price > 0 && (
          <div className="mao-sub">
            Purchase price: {fmt(deal.purchase_price)} —{' '}
            <span className={deal.purchase_price <= pl.mao ? 'positive' : 'negative'}>
              {deal.purchase_price <= pl.mao ? '✓ Under MAO' : '⚠ Over MAO'}
            </span>
          </div>
        )}
      </div>

      <div style={{marginTop:16}}>
        <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'var(--text-muted)',marginBottom:6}}>
          <span>Line item</span>
          <span style={{display:'flex',gap:12}}>
            <span>Actual</span><span>Budgeted</span>
          </span>
        </div>

        <div className="pl-row">
          <span>ARV / Sale Price</span>
          <span className="dual">
            {deal.actual_sale_price && <span className="money">{fmt(deal.actual_sale_price)}</span>}
            <span className="money">{fmt(pl.arv)}</span>
          </span>
        </div>
        <Row label="Purchase Price"   budgeted={pl.purchasePrice} />
        <Row label="Front-End Costs"  budgeted={pl.feBudgeted}   actual={pl.feActual}   indent />
        <Row label="SOW / Rehab"      budgeted={pl.rehabTotal}   actual={pl.sowActual > 0 ? pl.sowActual : null} indent />
        <Row label={`  ↳ Contingency (${deal.contingency_pct || 10}%)`} budgeted={pl.contingency} indent isBenchmark />
        <Row label="Holding Costs"    budgeted={pl.hcBudgeted}   actual={pl.hcActual}   indent />
        <Row label="Back-End Costs"   budgeted={pl.beBudgeted}   actual={pl.beActual}   indent />
        {deal.has_partner && (
          <Row label="Capital Partner Payout" budgeted={pl.cpPayout} indent />
        )}

        <div className="pl-row subtotal">
          <span>Total Costs</span>
          <span className="dual">
            <span className="money">{fmt(pl.totalCostsActual)}</span>
            <span className="money">{fmt(pl.totalCostsBudgeted)}</span>
          </span>
        </div>

        <div className="pl-row grand">
          <span>Net Proceeds</span>
          <span className={`money ${netColor}`}>{fmt(pl.netBudgeted)}</span>
        </div>

        {/* Profit % + ROI metrics */}
        <div style={{display:'flex',gap:16,marginTop:12}}>
          <div style={{flex:1,background:'var(--bg)',borderRadius:'var(--radius)',padding:'10px 12px'}}>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:2}}>Profit % of ARV</div>
            <div style={{fontWeight:700,fontSize:15}} className={profitOk ? 'positive' : 'negative'}>
              {pct(pl.profitPctBudgeted)}
              <span style={{fontSize:11,fontWeight:400,color:'var(--text-muted)',marginLeft:4}}>
                target {pct(deal.profit_pct || 20, 0)}
              </span>
            </div>
          </div>
          <div style={{flex:1,background:'var(--bg)',borderRadius:'var(--radius)',padding:'10px 12px'}}>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:2}}>ROI</div>
            <div style={{fontWeight:700,fontSize:15}}>{pct(pl.roiBudgeted)}</div>
          </div>
        </div>

        {/* ── Cash to Close ── */}
        <div style={{marginTop:16,background:'var(--bg)',borderRadius:'var(--radius)',padding:'12px 14px'}}>
          <div style={{fontWeight:700,fontSize:12,textTransform:'uppercase',
            letterSpacing:'0.05em',color:'var(--text-muted)',marginBottom:8}}>
            Cash to Close
          </div>

          {/* 3 Shacks loan + 75% ARV check */}
          {(pl.loan > 0 || deal.arv > 0) && (
            <div style={{marginBottom:8,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>
              <InfoLine label="HM Loan (90% PP + Rehab)" value={fmt(pl.loan)} />
              <InfoLine
                label="75% ARV Check"
                value={deal.arv > 0
                  ? (pl.arvQualOk ? `✓ Qualifies (max ${fmt(pl.arvCheck75)})` : `⚠ Exceeds (max ${fmt(pl.arvCheck75)})`)
                  : '— Enter ARV'}
                color={deal.arv > 0 ? (pl.arvQualOk ? 'var(--success)' : 'var(--danger)') : 'var(--text-muted)'}
              />
            </div>
          )}

          <InfoLine label={`Down Payment (10%)`}        value={fmt(pl.downPayment)} />
          <InfoLine label={`Title / Closing Costs (3%)`} value={fmt(pl.titleCosts)} />
          <InfoLine label={`Points (${deal.hm_points || 2}%)`} value={fmt(pl.points)} />
          <InfoLine label="Insurance"                    value={fmt(pl.insurance)} />
          <InfoLine
            label={`Prepaid Interest${hasPurchaseDate ? ` (${pl.daysLeft}d remaining)` : ''}`}
            value={hasPurchaseDate ? fmt(pl.prepaidInterest) : '— Set purchase date'}
          />

          <div style={{display:'flex',justifyContent:'space-between',fontSize:13,
            marginTop:8,paddingTop:8,borderTop:'1px solid var(--border)',fontWeight:700}}>
            <span>Total Cash Needed</span>
            <span style={{color:'var(--brand)'}}>{fmt(pl.cashToClose)}</span>
          </div>
        </div>

        {/* Cash on Cash Return */}
        <div style={{display:'flex',gap:16,marginTop:12}}>
          <div style={{flex:1,background:'var(--bg)',borderRadius:'var(--radius)',padding:'10px 12px'}}>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:2}}>Cash on Cash Return</div>
            <div style={{fontWeight:700,fontSize:15,
              color: pl.cocReturn >= 30 ? 'var(--success)' : pl.cocReturn >= 15 ? 'var(--text)' : 'var(--danger)'}}>
              {pl.cashToClose > 0 ? pct(pl.cocReturn) : '—'}
            </div>
          </div>
          <div style={{flex:1,background:'var(--bg)',borderRadius:'var(--radius)',padding:'10px 12px'}}>
            <div style={{fontSize:11,color:'var(--text-muted)',marginBottom:2}}>Net / Cash to Close</div>
            <div style={{fontSize:11,color:'var(--text-muted)'}}>
              {pl.cashToClose > 0 ? `${fmt(pl.netBudgeted)} / ${fmt(pl.cashToClose)}` : '—'}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
